import { Box, Button, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useState } from "react";
import { Container } from "@mui/material";
import { Form } from "react-bootstrap";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { RiUserAddFill } from "react-icons/ri";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import UsersTable from "../../components/datagrid/UsersTable";
import apis from "../../services";
import useNavigate from "hooks/Navigate";
import Link from "hooks/Link";

const PAGE_LIMIT = 20;
const DEFAULT_STATS = {
  totalUsers: 0,
  totalInvestors: 0,
  yesterdayUsers: 0,
  yesterdayInvestors: 0,
};

const statCards = [
  { key: "totalUsers", label: "Total Users" },
  { key: "totalInvestors", label: "Total Investors" },
  { key: "yesterdayUsers", label: "New Users from Yesterday" },
  { key: "yesterdayInvestors", label: "New Investors from Yesterday" },
];

const filterTabs = [
  { key: "all", label: "All Users" },
  { key: "investor", label: "Investor" },
  { key: "jtc_employee", label: "JTC Employee" },
];

export const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: PAGE_LIMIT,
    totalPages: 1,
  });
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showDiv, setShowDiv] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchAllUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apis.getAllUser({
        search,
        page,
        limit: PAGE_LIMIT,
        category: selectedCategory === "all" ? "" : selectedCategory,
        includeDeleted: true,
      });

      if (res?.data?.status) {
        setUsers(res?.data?.users || []);
        setPagination(
          res?.data?.pagination || {
            total: res?.data?.users?.length || 0,
            page,
            limit: PAGE_LIMIT,
            totalPages: 1,
          }
        );
      } else {
        toast.error(res?.data?.message || "Unable to load users");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load users");
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedCategory]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await apis.getUserOverviewStats();
      if (res?.data?.status) {
        setStats(res?.data?.data || DEFAULT_STATS);
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load user stats");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const copyToClipboard = (address) => {
    if (!address) return;
    navigator.clipboard.writeText(address);
    setShowDiv(true);

    setTimeout(() => {
      setShowDiv(false);
    }, 1000);
  };

  const onPageChange = (direction) => {
    if (direction === "prev" && page === 1) return;
    if (direction === "next" && page >= pagination.totalPages) return;

    setPage((currentPage) =>
      direction === "next" ? currentPage + 1 : currentPage - 1
    );
  };

  const handleCategoryChange = (category) => {
    setPage(1);
    setSelectedCategory(category);
  };

  const { rows, columns } = UsersTable(users, {
    navigate,
    copyToClipboard,
    offset: (page - 1) * PAGE_LIMIT,
  });

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: "100vh",
            overflow: "auto",
          }}
          className="custom-scroll"
        >
          <Header />

          {loading ? (
            <div className="loader-container">
              <div className="loader" />
            </div>
          ) : (
            <Container sx={{ mt: 4, mb: 4 }} className="mainContainerComp">
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 2.5,
                }}
              >
                <Box>
                  <h3 className="dashboard-title" style={{ marginBottom: 6 }}>
                    {t("titles.users")}
                  </h3>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Manage investors and employee accounts from one place.
                  </Typography>
                </Box>

                <Link to={"/admin/users/add"} className="lp-btn adduser">
                  {t("buttons.add_user")}
                  <RiUserAddFill />
                </Link>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mb: 2.5 }}
              >
                {filterTabs.map((item) => (
                  <Button
                    key={item.key}
                    onClick={() => handleCategoryChange(item.key)}
                    variant={selectedCategory === item.key ? "contained" : "outlined"}
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      px: 2.25,
                      ...(selectedCategory === item.key
                        ? {
                            background:
                              "linear-gradient(135deg, #E60E10 0%, #ff6b4a 100%)",
                            boxShadow: "none",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #cc0d0f 0%, #ff5b35 100%)",
                              boxShadow: "none",
                            },
                          }
                        : {
                            borderColor: "rgba(15, 23, 42, 0.14)",
                            color: "#111827",
                            "&:hover": {
                              borderColor: "#E60E10",
                              backgroundColor: "rgba(230, 14, 16, 0.04)",
                            },
                          }),
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {statCards.map((item) => (
                  <Grid item xs={12} sm={6} lg={3} key={item.key}>
                    <Card
                      sx={{
                        borderRadius: 4,
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ textTransform: "uppercase", letterSpacing: 1, color: "text.secondary", fontWeight: 700 }}
                          >
                            {item.label}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            {statsLoading ? "..." : Number(stats?.[item.key] || 0).toLocaleString()}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Form.Group className="my-2" style={{ position: "relative" }}>
                <Form.Label>Search by name, email, or wallet ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter name, email, or wallet ID"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <div className="searchCon">
                  <CiSearch className="search-icon" color="#AFAEAE" fontSize={18} />
                </div>
              </Form.Group>

              <Grid
                item
                container
                justifyContent="flex-end"
                className="mt-4"
                sx={{ height: "600px", position: "relative", zIndex: "1" }}
              >
                {showDiv && (
                  <div className={`transition-div ${showDiv ? "show" : ""}`}>
                    <p>Wallet ID copied successfully</p>
                  </div>
                )}

                <DataGrid
                  rows={rows}
                  columns={columns}
                  hideFooter
                  disableSelectionOnClick
                  pageSize={PAGE_LIMIT}
                  rowsPerPageOptions={[PAGE_LIMIT]}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                  }
                />
              </Grid>

              <div className="pagination-container">
                <div>Total: {pagination.total}</div>
                <div>
                  <button
                    className="paginator"
                    disabled={page === 1}
                    onClick={() => onPageChange("prev")}
                  >
                    <IoIosArrowBack />
                  </button>
                  Page {pagination.page} of {pagination.totalPages}
                  <button
                    className="paginator"
                    disabled={page >= pagination.totalPages}
                    onClick={() => onPageChange("next")}
                  >
                    <IoIosArrowForward />
                  </button>
                </div>
              </div>
            </Container>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default Users;
